import React from 'react';
import Icon from '../../../components/AppIcon';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import EmptyState from '../../../components/ui/EmptyState';

const RAGBadge = ({ status }) => {
  const config = {
    green: { label: 'Green', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    amber: { label: 'Amber', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
    red: { label: 'Red', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
    compliant: { label: 'Compliant', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    warning: { label: 'Warning', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
    critical: { label: 'Critical', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  };
  const c = config?.[status] || config?.green;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${c?.bg} ${c?.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c?.dot}`} />
      {c?.label}
    </span>
  );
};

const SectionBlock = ({ title, icon, children, count }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
      <Icon name={icon} size={16} className="text-accent" />
      <h3 className="text-sm font-semibold text-foreground font-heading">{title}</h3>
      {count !== undefined && (
        <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{count} items</span>
      )}
    </div>
    {children}
  </div>
);

const ReportPreview = ({ reportData, selectedModules, siteName, dateFrom, dateTo, reportType, isLoading }) => {
  const today = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const fromLabel = dateFrom ? new Date(dateFrom)?.toLocaleDateString('en-GB') : 'All time';
  const toLabel = dateTo ? new Date(dateTo)?.toLocaleDateString('en-GB') : today;

  const reportTypeLabels = {
    compliance: 'Compliance Report',
    progress: 'Project Progress Summary',
    audit: 'Audit Findings Report',
    worker_performance: 'Worker Performance Analytics',
    custom: 'Custom Report',
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 flex flex-col items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" label="Generating report preview..." />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="bg-card border border-border rounded-xl min-h-[400px] flex items-center justify-center">
        <EmptyState
          icon="FileBarChart"
          title="No Preview Yet"
          description='Configure your report settings on the left and click "Generate Report" to see a preview here.'
        />
      </div>
    );
  }

  return (
    <div id="report-preview-content" className="bg-white border border-border rounded-xl overflow-hidden">
      {/* Report Header */}
      <div className="bg-slate-800 text-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <Icon name="HardHat" size={16} color="white" />
              </div>
              <span className="text-sm font-semibold text-slate-300">SiteProof</span>
            </div>
            <h1 className="text-xl font-bold font-heading">{reportTypeLabels?.[reportType] || 'Report'}</h1>
            <p className="text-slate-300 text-sm mt-1">{siteName || 'All Sites'}</p>
          </div>
          <div className="text-right text-sm text-slate-300">
            <p>Generated: {today}</p>
            <p>Period: {fromLabel} &ndash; {toLabel}</p>
          </div>
        </div>

        {/* Summary metrics row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Tasks', value: reportData?.tasks?.length || 0, icon: 'CheckSquare' },
            { label: 'Incidents', value: reportData?.incidents?.length || 0, icon: 'ShieldAlert' },
            { label: 'Snags', value: reportData?.snags?.length || 0, icon: 'AlertTriangle' },
            { label: 'Documents', value: reportData?.documents?.length || 0, icon: 'FileText' },
          ]?.map(m => (
            <div key={m?.label} className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name={m?.icon} size={13} className="text-slate-300" />
                <span className="text-xs text-slate-300">{m?.label}</span>
              </div>
              <p className="text-xl font-bold">{m?.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-2">
        {/* Executive Summary */}
        <SectionBlock title="Executive Summary" icon="FileBarChart" count={undefined}>
          <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 leading-relaxed">
            <p>
              This report covers the period <strong>{fromLabel}</strong> to <strong>{toLabel}</strong> for{' '}
              <strong>{siteName || 'all sites'}</strong>. A total of{' '}
              <strong>{reportData?.tasks?.length || 0}</strong> tasks,{' '}
              <strong>{reportData?.incidents?.length || 0}</strong> incidents,{' '}
              <strong>{reportData?.snags?.length || 0}</strong> snags, and{' '}
              <strong>{reportData?.documents?.length || 0}</strong> documents were recorded during this period.
            </p>
          </div>
        </SectionBlock>

        {/* Tasks Section */}
        {selectedModules?.includes('tasks') && (reportData?.tasks?.length || 0) > 0 && (
          <SectionBlock title="Tasks" icon="CheckSquare" count={reportData?.tasks?.length}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left p-2 font-semibold text-slate-600">Task</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Category</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Status</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Assigned To</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.tasks?.slice(0, 8)?.map((task, i) => (
                    <tr key={task?.id || i} className="border-t border-slate-100">
                      <td className="p-2 text-slate-700 font-medium">{task?.title || 'Task'}</td>
                      <td className="p-2 text-slate-500">{task?.category || '—'}</td>
                      <td className="p-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          task?.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          task?.status === 'pending'? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>{task?.status || 'pending'}</span>
                      </td>
                      <td className="p-2 text-slate-500">{task?.assigned_user?.full_name || '—'}</td>
                      <td className="p-2 text-slate-500">
                        {task?.date ? new Date(task?.date)?.toLocaleDateString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(reportData?.tasks?.length || 0) > 8 && (
                <p className="text-xs text-muted-foreground mt-2 px-2">+{reportData?.tasks?.length - 8} more tasks in full PDF report</p>
              )}
            </div>
          </SectionBlock>
        )}

        {/* Documents Section */}
        {selectedModules?.includes('documents') && reportData?.documents?.length > 0 && (
          <SectionBlock title="Documents" icon="FileText" count={reportData?.documents?.length}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left p-2 font-semibold text-slate-600">Title</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Category</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Status</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.documents?.slice(0, 8)?.map((doc, i) => (
                    <tr key={doc?.id || i} className="border-t border-slate-100">
                      <td className="p-2 text-slate-700 font-medium">{doc?.title || doc?.name || 'Untitled'}</td>
                      <td className="p-2 text-slate-500">{doc?.category || '—'}</td>
                      <td className="p-2">
                        <RAGBadge status={doc?.status === 'valid' ? 'green' : doc?.status === 'expired' ? 'red' : 'amber'} />
                      </td>
                      <td className="p-2 text-slate-500">
                        {doc?.expiry_date ? new Date(doc?.expiry_date)?.toLocaleDateString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reportData?.documents?.length > 8 && (
                <p className="text-xs text-muted-foreground mt-2 px-2">+{reportData?.documents?.length - 8} more documents in full report</p>
              )}
            </div>
          </SectionBlock>
        )}

        {/* Incidents Section */}
        {selectedModules?.includes('incidents') && reportData?.incidents?.length > 0 && (
          <SectionBlock title="Incidents" icon="ShieldAlert" count={reportData?.incidents?.length}>
            <div className="space-y-2">
              {reportData?.incidents?.slice(0, 5)?.map((inc, i) => (
                <div key={inc?.id || i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    inc?.severity === 'critical' ? 'bg-red-500' :
                    inc?.severity === 'high' ? 'bg-orange-500' :
                    inc?.severity === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700">{inc?.title || inc?.description || 'Incident'}</p>
                    <p className="text-xs text-slate-500">
                      {inc?.incident_date ? new Date(inc?.incident_date)?.toLocaleDateString('en-GB') : '—'} · {inc?.severity || 'low'} severity
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        )}

        {/* Snags Section */}
        {selectedModules?.includes('snags') && reportData?.snags?.length > 0 && (
          <SectionBlock title="Snags" icon="AlertTriangle" count={reportData?.snags?.length}>
            <div className="space-y-2">
              {reportData?.snags?.slice(0, 5)?.map((snag, i) => (
                <div key={snag?.id || i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <RAGBadge status={snag?.status === 'closed' ? 'green' : snag?.status === 'in_progress' ? 'amber' : 'red'} />
                  <p className="text-xs text-slate-700 flex-1">{snag?.description || snag?.title || 'Snag item'}</p>
                  <span className="text-xs text-slate-400">
                    {snag?.created_at ? new Date(snag?.created_at)?.toLocaleDateString('en-GB') : '—'}
                  </span>
                </div>
              ))}
            </div>
          </SectionBlock>
        )}

        {/* Photos Section */}
        {selectedModules?.includes('photos') && (reportData?.photos?.length || 0) > 0 && (
          <SectionBlock title="Photo Evidence" icon="Camera" count={reportData?.photos?.length}>
            <div className="grid grid-cols-3 gap-2">
              {reportData?.photos?.slice(0, 6)?.map((photo, i) => (
                <div key={photo?.id || i} className="bg-slate-100 rounded-lg aspect-square flex flex-col items-center justify-center p-2 text-center">
                  <Icon name="Image" size={20} className="text-slate-400 mb-1" />
                  <p className="text-xs text-slate-500 truncate w-full">{photo?.sourceTitle || 'Photo'}</p>
                  <p className="text-xs text-slate-400">{photo?.source}</p>
                </div>
              ))}
            </div>
            {(reportData?.photos?.length || 0) > 6 && (
              <p className="text-xs text-muted-foreground mt-2">+{reportData?.photos?.length - 6} more photos included in PDF export</p>
            )}
          </SectionBlock>
        )}

        {/* Certificates Section */}
        {selectedModules?.includes('certificates') && reportData?.certificates?.length > 0 && (
          <SectionBlock title="Certificates" icon="Award" count={reportData?.certificates?.length}>
            <div className="grid grid-cols-2 gap-2">
              {reportData?.certificates?.slice(0, 6)?.map((cert, i) => (
                <div key={cert?.id || i} className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs font-semibold text-slate-700">{cert?.title || cert?.name || 'Certificate'}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Expires: {cert?.expiry_date ? new Date(cert?.expiry_date)?.toLocaleDateString('en-GB') : 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </SectionBlock>
        )}

        {/* Audit Trail Note */}
        <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={14} className="text-slate-500" />
            <p className="text-xs text-slate-500">
              This report was generated by SiteProof on {today}. All data is sourced directly from the live platform database.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;
